<template>
  <Layout hide-mobile-menu class="layout--doc-preview">
    <HeaderPreviewPage :doc="doc" />
    <div class="layout__wrapper">
      <BackLink class="doc-preview__backlink container container--small" />

      <div class="layout__wrapper-inner container container--small">
        <section class="doc-preview">
          <div class="doc-preview__cover">
            <div class="doc-preview__info">
              <BookCardThumbnail
                :doc="doc"
                class="doc-preview__img-box"
                coverClass="doc-preview__img"
                fallbackCoverClass="doc-preview__img-fallback"
              >
                <ProgressBar :doc="doc" />
              </BookCardThumbnail>
              <div class="doc-preview__content">
                <div class="doc-preview__title-box">
                  <ContentLabel
                    v-if="doc.showAiLabel"
                    class="doc-preview__content-label"
                  />
                  <h3 v-else-if="hasTags()" class="doc-preview__category">
                    {{ doc.tags[0] }}
                  </h3>
                  <h1 class="doc-preview__title">{{ doc.title }}</h1>
                  <div
                    v-if="doc.doc_group"
                    class="doc-preview__author doc-preview__author--flex"
                  >
                    <div class="doc-preview__author-inner">
                      <span>By </span>
                      <router-link
                        class="doc-preview__author-text"
                        :to="{
                          name: 'podcastPage',
                          params: {
                            url_slug: doc.doc_group.url_slug,
                          },
                        }"
                        >{{ doc.doc_group.title }}</router-link
                      >
                    </div>
                    <PodcastFollowButton
                      v-if="podcastFollowView"
                      class="doc-preview__follow-button"
                      :view="podcastFollowView"
                      :useLinkVariant="true"
                    />
                  </div>
                  <h2 v-else class="doc-preview__author">
                    By {{ doc.author }}
                  </h2>
                </div>
                <div class="doc-preview__more">
                  <div class="doc-preview__control-box">
                    <div class="doc-preview__control-left icon-toolbar">
                      <AppButton
                        class="doc-preview__read-btn icon-toolbar__item"
                        tag="router-link"
                        :icon="readingButton.icon"
                        :to="readLink"
                      >
                        {{ readingButton.text }}
                      </AppButton>
                      <DocListsMenu
                        class="doc-preview-favorite-btn icon-toolbar__item"
                        :doc="doc"
                        position="center"
                        useIconButton
                      />
                    </div>
                    <div class="doc-preview__control-right">
                      <ExportBookButton
                        v-if="doc.showDownload"
                        class="doc-preview-download-btn icon-toolbar__item"
                        :doc="doc"
                      />
                      <DocLibraryMenu
                        class="doc-preview-actions-btn icon-toolbar__item"
                        :doc="doc"
                      >
                        <IconButton icon="imore" tooltip="Actions" outlined />
                      </DocLibraryMenu>
                    </div>
                  </div>
                  <div v-if="doc.has_audio" class="doc-preview__data-box">
                    <div class="doc-preview__data">
                      <span
                        class="doc-preview__data-icon iconfont iaudio"
                      ></span>
                      <span class="doc-preview__data-text"
                        >Audio available</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="doc-preview__app">
              <div class="doc-preview__app-text">Install the App</div>
              <div class="doc-preview__qr-code-box">
                <img
                  class="doc-preview__qr-code"
                  src="@/assets.app/images/qr-code-download-app.svg"
                  alt="QR code"
                />
              </div>
              <a
                href="https://play.google.com/store/apps/details?id=com.shortform.app"
                target="_blank"
                class="doc-preview__app-btn google-store-btn"
              >
                <img
                  class="doc-preview__app-btn-img"
                  src="@/assets.app/images/google-play-store.svg"
                  alt="Google Play"
                />
              </a>
              <a
                href="https://apps.apple.com/us/app/shortform/id1489472007"
                target="_blank"
                class="doc-preview__app-btn apple-store-btn"
              >
                <img
                  class="doc-preview__app-btn-img"
                  src="@/assets.app/images/apple-store.svg"
                  alt="App Store"
                />
              </a>
            </div>
          </div>
          <div class="doc-preview__about-box">
            <h2 class="doc-preview__about-title">{{ doc.aboutTitle }}</h2>
            <div class="doc-preview__about-text" v-html="doc.blurb"></div>
            <ul class="doc-preview__additional-info-list">
              <li
                v-if="doc.original_publish_date"
                class="doc-preview__additional-info doc-preview__additional-info--publish-date"
              >
                <span class="doc-preview__additional-info-title"
                  >Publish date:</span
                >
                {{ formatDate(doc.original_publish_date) }}
              </li>
            </ul>
          </div>
          <div class="doc-preview__mobile-read-btn-box">
            <AppButton
              class="doc-preview__read-btn icon-toolbar__item"
              tag="router-link"
              :icon="readingButton.icon"
              :to="readLink"
            >
              {{ readingButton.text }}
            </AppButton>
          </div>
        </section>

        <PreviewPageHighlights :doc="doc" />
      </div>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { ComputedRef, Ref, computed, onMounted, ref } from 'vue'
import { DocView } from '@/models/doc'

import { PodcastFollowView } from '@/models/docs.list.podcasts.episodes'

import { formatDate } from '@/helpers/time'
import Layout from '@/app/components/Layout.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import BackLink from '@/app/components/BackLink.vue'
import PreviewPageHighlights from '@/app/components/doc/preview/Highlights.vue'
import ProgressBar from '@/app/components/doc/preview/ProgressBar.vue'
import HeaderPreviewPage from '@/app/components/doc/preview/HeaderPreviewPage.vue'
import DocLibraryMenu from '@/app/components/doc/DocLibraryMenu.vue'
import DocListsMenu from '@/app/components/doc/DocListsMenu.vue'
import ExportBookButton from '@/app/components/doc/preview/ExportBookButton.vue'
import ContentLabel from '@/app/components/doc/ContentLabel.vue'
import BookCardThumbnail from '@/app/components/book/card/BookCardThumbnail.vue'
import PodcastFollowButton from '@/app/components/listing/PodcastFollowButton.vue'
import IconButton from '@/app/components/button/IconButton.vue'

const props = defineProps<{
  doc: DocView
}>()

const readingButton = computed(() => props.doc.startReadingButton())
const readLink = computed(() => props.doc.startLinkParams())

function hasTags(): ComputedRef<boolean> {
  return computed(() => {
    return props.doc.tags.length > 0
  })
}

const podcastFollowView: Ref<PodcastFollowView | null> = ref(null)

onMounted(() => {
  if (!props.doc.doc_group || !props.doc.isDocGroupFollowable()) {
    return
  }

  const docGroup = props.doc.doc_group
  podcastFollowView.value = new PodcastFollowView(
    docGroup.is_followed,
    docGroup.id,
  )
})
</script>

<style lang="scss" scoped>
.layout--doc-preview {
  // To fit "Read Now"/"Continue reading" button
  padding-bottom: 100px;
}

.doc-preview {
  margin-bottom: 48px;

  &__backlink {
    margin-block: 24px;

    @media only screen and (max-width: $w-tablet-s) {
      display: none;
    }
  }
  &__cover {
    display: flex;
    @media only screen and (max-width: $w-tablet-s) {
      @include full-width;
      padding-top: 85px;
      padding-bottom: 20px;
      border-radius: 0 0 14px 14px;
      background: $c-black-5;
    }
  }
  &__info {
    display: flex;
    flex: 1;
    min-width: 0;
    @media only screen and (max-width: $w-tablet-s) {
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 100%;
      text-align: center;
    }
  }
  &__img-box {
    width: 234px;
    flex-shrink: 0;
    margin-right: 32px;
    border-radius: 14px;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    @media only screen and (max-width: $w-tablet-s) {
      flex-direction: column;
      width: 200px;
      margin-right: 0;
      margin-bottom: 60px;
      border-radius: 10px;
      background: transparent;
      overflow: visible;
    }
    @media only screen and (max-width: $w-medium-s) {
      width: 154px;
      margin-bottom: 24px;
    }
    @media only screen and (min-width: $w-tablet-b) {
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  }
  :deep(.doc-preview__img) {
    display: block;
    width: 100%;
    max-width: var(--cover-width);
    z-index: 1;
    filter: drop-shadow(0px 3px 10px rgba(0, 0, 0, 0.13));
    @media only screen and (max-width: $w-tablet-s) {
      max-width: none;
    }
  }
  :deep(.doc-preview__img),
  :deep(.doc-preview__img-fallback) {
    --cover-width: 154px;
  }
  :deep(.book-thumbnail__cover--background) {
    @media only screen and (max-width: $w-tablet-s) {
      display: none;
    }
  }
  :deep(.doc-progress) {
    margin-top: 20px;
    @media only screen and (max-width: $w-tablet-s) {
      margin-top: 36px;
    }
  }
  &__content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 28px 0;
    min-width: 0;
    width: 100%;
    @media only screen and (max-width: $w-tablet-s) {
      padding: 0;
      align-items: center;
    }
  }
  &__content-label {
    margin-bottom: 20px;
    @media only screen and (max-width: $w-tablet-s) {
      order: 1;
      margin-bottom: 0;
      margin-top: 20px;
    }
  }
  &__category {
    font-size: 13px;
    line-height: 15px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $c-black-9;
    margin-bottom: 28px;
    @media only screen and (max-width: $w-tablet-s) {
      display: none;
    }
  }
  &__title-box {
    width: 100%;
    margin-bottom: 24px;
    @media only screen and (max-width: $w-tablet-s) {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 68px;
    }
    @media only screen and (max-width: $w-medium-s) {
      margin-bottom: 80px;
    }
    @media screen and (max-width: $w-mobile-s) {
      margin-bottom: 58px;
    }
  }
  &__title {
    font-weight: 500;
    font-size: 32px;
    line-height: 44px;
    letter-spacing: -0.02em;
    color: $c-black-2;
    margin-bottom: 8px;
    @media only screen and (max-width: $w-tablet-s) {
      font-size: 24px;
      line-height: 32px;
      margin-bottom: 16px;
    }
  }
  &__author {
    font-weight: 400;
    font-size: 18px;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: $c-black-3;
    @media only screen and (max-width: $w-tablet-s) {
      font-size: 16px;
      line-height: 18px;
    }
    @media only screen and (max-width: $w-mobile-s) {
      font-size: 14px;
      line-height: 16px;
    }
  }
  &__author--flex {
    display: flex;
    gap: 8px;
    width: 100%;
    @media only screen and (max-width: $w-tablet-s) {
      justify-content: center;
    }
  }
  &__author-inner {
    display: flex;
    gap: 5px;
    min-width: 0;
  }
  &__author-text {
    overflow: hidden;
    text-wrap: nowrap;
    text-overflow: ellipsis;
  }
  &__follow-button {
    display: flex;
    align-items: center;
    gap: 8px;

    &:before {
      content: '';
      display: block;
      width: 4px;
      height: 4px;
      border-radius: 4px;
      margin-bottom: 2px;
      background: $c-black-13;
    }
  }
  :deep(.podcast-follow-btn__hint) {
    @media only screen and (max-width: $w-largest) {
      left: calc(50% - (var(--follow-btn-width) / 2));
    }
    @media only screen and (max-width: $w-tablet-s) {
      left: var(--container-padding);
    }
  }
  &__control-box {
    display: flex;
    @media only screen and (max-width: $w-tablet-s) {
      display: none;
    }
  }
  &__control-left,
  &__control-right {
    display: flex;
    width: 100%;
    flex-basis: 0;
    flex-grow: 1;
    justify-content: space-between;
  }
  &__control-left {
    flex-grow: 0;
    .icon-btn {
      margin-left: 20px;
    }
  }
  &__control-right {
    max-width: 160px;
    margin-left: 41px;
    position: relative;
    justify-content: flex-start;

    &:before {
      content: '';
      display: block;
      width: 1px;
      height: 100%;
      position: absolute;
      top: 0;
      left: -21px;
      background-color: $c-border;
    }
  }
  &__data-box {
    display: flex;
    align-items: center;
    margin-top: 30px;
    @media only screen and (max-width: $w-tablet-s) {
      text-align: center;
      justify-content: center;
      margin-top: 40px;
    }
  }
  &__data {
    display: flex;
    align-items: flex-end;
    font-size: 14px;
    line-height: 16px;
    color: $c-black-9;
    margin-right: 20px;
    &:last-child {
      margin-right: 0;
    }
  }
  &__data-icon {
    font-size: 24px;
    margin-right: 8px;
  }
  &__data-text {
    margin-bottom: 2px;
  }
  &__app {
    width: 120px;
    flex-shrink: 0;
    margin-left: 49px;
    position: relative;
    text-align: center;
    &:before {
      content: '';
      display: block;
      width: 1px;
      height: 100%;
      position: absolute;
      top: 0;
      left: -25px;
      background-color: $c-border;
    }
    @media only screen and (max-width: $w-tablet-s) {
      display: none;
    }
  }
  &__app-text {
    font-size: 14px;
    line-height: 18px;
    color: $c-black-1;
    margin-bottom: 29px;
    margin-top: 20px;
  }
  &__qr-code-box {
    background: $c-white;
    box-shadow: 0px 0px 32px rgba(41, 41, 41, 0.08);
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 31px;
    width: 106px;
    margin-left: 8px;
  }
  &__qr-code {
    display: block;
    height: 104px;
    width: 104px;
  }
  &__app-btn {
    display: block;
    margin-bottom: 12px;
    &:last-child {
      margin-bottom: 0;
    }
    @include hover {
      filter: drop-shadow(0px 1px 5px rgba(0, 0, 0, 0.11))
        drop-shadow(0px 8px 16px rgba(20, 28, 49, 0.15));
    }
  }
  &__app-btn-img {
    display: block;
  }
  &__about-box {
    margin-top: 72px;
    overflow: hidden;
    @media only screen and (max-width: $w-large-s) {
      margin-top: 40px;
    }
  }
  &__about-title {
    font-weight: 500;
    font-size: 24px;
    line-height: 32px;
    color: $c-black-2;
    margin-bottom: 12px;
    @media only screen and (max-width: $w-large-s) {
      line-height: 28px;
    }
  }
  &__about-text {
    color: $c-black-3;
    :deep(p) {
      font-weight: 400;
      font-size: 18px;
      line-height: 28px;
      letter-spacing: -0.02em;
    }

    :deep(ol) {
      margin-bottom: 20px;
      padding-left: 18px;
      // counter-reset: item;
      @media only screen and (max-width: $w-tablet-s) {
        margin-bottom: 43px;
      }
      @media only screen and (max-width: $w-mobile-s) {
        margin-bottom: 36px;
      }
      li {
        font-size: 18px;
        line-height: 32px;
        letter-spacing: -0.02em;
        font-weight: 400;

        padding-left: 6px;
        ol,
        ul {
          padding-left: 35px;
          padding-top: 24px;
          @media only screen and (max-width: $w-tablet-s) {
            padding-left: 30px;
            padding-top: 25px;
            margin-bottom: 30px;
          }
          @media only screen and (max-width: $w-mobile-s) {
            padding-left: 20px;
            padding-top: 12px;
            margin-bottom: 20px;
          }
          li {
            @media only screen and (max-width: $w-tablet-s) {
              margin-bottom: 20px;
            }
          }
        }
      }
    }

    :deep(ul) {
      margin-bottom: 20px;
      padding-left: 32px;
      @media only screen and (max-width: $w-tablet-s) {
        margin-bottom: 30px;
      }
      @media only screen and (max-width: $w-mobile-s) {
        padding-left: 20px;
        padding-top: 12px;
      }
      li {
        font-size: 18px;
        line-height: 28px;
        letter-spacing: -0.02em;
        padding-left: 16px;
        position: relative;
        margin-bottom: 4px;
        font-weight: 400;

        &:before {
          content: '';
          display: block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: $c-black-2;
          position: absolute;
          left: 0;
          top: 11px;
          @media only screen and (max-width: $w-tablet-s) {
            top: 11px;
          }
          @media only screen and (max-width: $w-mobile-s) {
            top: 8px;
          }
        }
      }
      ul,
      ol {
        padding-left: 35px;
        padding-top: 15px;
        @media only screen and (max-width: $w-tablet-s) {
          padding-left: 30px;
          padding-top: 20px;
        }
        @media only screen and (max-width: $w-mobile-s) {
          padding-left: 20px;
          padding-top: 12px;
        }
      }
    }
  }
  &__additional-info-list {
    padding: 0;
    margin-top: 24px;
    &:empty {
      display: none;
    }
  }
  &__additional-info {
    font-weight: 500;
    color: $c-black-3;
  }
  &__additional-info-title {
    color: $c-black-2;
  }
  &__mobile-read-btn-box {
    display: none;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 3;
    width: 100%;
    padding-bottom: 40px;
    background: linear-gradient(
      0deg,
      hsla(0, 0%, 100%, 0.9),
      hsla(0, 0%, 100%, 0)
    );
    @media only screen and (max-width: $w-tablet-s) {
      display: block;
    }
    @media only screen and (max-width: $w-mobile-s) {
      padding-inline: 28px;
    }

    .doc-preview__read-btn {
      display: block;
      width: fit-content;
      margin-inline: auto;
      padding: 7px 72px;
      font-size: 18px;
      @media only screen and (max-width: $w-mobile-s) {
        font-size: 16px;
        width: 100%;
      }
    }
  }
}

.layout--podcast .doc-preview {
  &__img-box {
    padding: 24px;
    @media only screen and (max-width: $w-tablet-s) {
      padding: 0;
    }
    @media only screen and (max-width: $w-medium-s) {
      width: 200px;
    }
  }
  :deep(.doc-preview__img) {
    max-width: none;
    border-radius: 8px;
  }
}

.night-mode {
  .doc-preview {
    &__cover {
      @media only screen and (max-width: $w-tablet-s) {
        background: $c-dark-2;
      }
    }
    &__img-box {
      @media only screen and (max-width: $w-tablet-s) {
        background: transparent;
      }
    }
    &__additional-info {
      color: $c-dark-7;
    }
    &__additional-info-title {
      color: $c-black-11;
    }
    &__mobile-read-btn-box {
      background: linear-gradient(0deg, #1d1e20, rgba(29, 30, 32, 0));
    }
    &__category {
      color: $c-dark-8;
    }
    &__title {
      color: $c-white;
    }
    &__author {
      color: $c-dark-4 !important;
    }
    &__control-right {
      &:before {
        background-color: $c-dark-3;
      }
    }
    &__data {
      color: $c-dark-8;
    }
    &__app {
      &:before {
        background-color: $c-dark-3;
      }
    }
    &__app-text {
      color: $c-dark-4;
    }
    &__about-title {
      color: $c-white;
    }
    &__about-text,
    &__about-text :deep(p) {
      color: $c-dark-4;
    }
    &__continue-btn {
      color: $c-black-2 !important;
    }
  }
}
</style>
